'use client';

/**
 * useNotificationSound Hook
 *
 * Manages notification sound playback including:
 * - Playing sounds for new notifications
 * - Sound selection and preview
 * - Volume control
 * - Respecting quiet hours and user preferences
 *
 * Storage: Syncs to Supabase user_preferences table with localStorage cache.
 * Uses useUserPreferencesObject for local-first updates with background sync.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { NOTIFICATION_SOUNDS } from '@/store/slices/notificationsTypes';
import { useUserPreferencesObject } from '@/hooks/syncHooks';
import logger from '@/lib/logger';

const SOUNDS_PATH = '/notification-sounds';

/**
 * Play a simple beep using Web Audio API as fallback when sound files are missing
 */
function playFallbackBeep(volume: number = 0.5, frequency: number = 440, duration: number = 0.15): Promise<void> {
  return new Promise((resolve) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      // Quick fade in/out to avoid clicks
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume * 0.3, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);

      oscillator.onended = () => {
        audioContext.close();
        resolve();
      };
    } catch {
      resolve();
    }
  });
}

interface UseNotificationSoundOptions {
  /** Initial volume (0-1) */
  initialVolume?: number;
  /** Default sound ID */
  defaultSound?: string;
  /** Whether sound is enabled */
  enabled?: boolean;
}

interface QuietHours {
  enabled: boolean;
  start: string; // HH:MM format
  end: string; // HH:MM format
}

/** Consolidated notification settings stored in Supabase */
interface NotificationSettings {
  volume: number;
  muted: boolean;
  sound: string;
  quietHours: QuietHours;
}

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  volume: 0.5,
  muted: false,
  sound: 'default',
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '07:00',
  },
};

export function useNotificationSound(options: UseNotificationSoundOptions = {}) {
  const { initialVolume = 0.5, defaultSound = 'default', enabled = true } = options;

  // Consolidated settings with Supabase sync
  // Local-first: localStorage for fast initial load, background sync to Supabase
  const [settings, setSettings] = useUserPreferencesObject<NotificationSettings>(
    'notification_settings',
    {
      ...DEFAULT_NOTIFICATION_SETTINGS,
      volume: initialVolume,
      sound: defaultSound,
    },
    { syncDebounceMs: 500 } // Sync quickly since these are explicit user actions
  );

  // Local playback state (not persisted)
  const [isPlaying, setIsPlaying] = useState(false);

  // Extract settings for easier access
  const { volume, muted: isMuted, sound: currentSound, quietHours } = settings;

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);

  /**
   * Initialize audio element
   */
  const initAudio = useCallback(() => {
    if (typeof window === 'undefined') return null;

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }

    return audioRef.current;
  }, [volume]);

  /**
   * Check if currently in quiet hours
   */
  const isInQuietHours = useCallback((): boolean => {
    if (!quietHours.enabled) return false;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = quietHours.start.split(':').map(Number);
    const [endHour, endMin] = quietHours.end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Handle overnight quiet hours (e.g., 22:00 - 07:00)
    if (startMinutes > endMinutes) {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }, [quietHours]);

  /**
   * Get sound file URL
   */
  const getSoundUrl = useCallback((soundId: string): string => {
    const sound = NOTIFICATION_SOUNDS.find((s) => s.id === soundId);
    // sound.file already contains the full path (e.g., '/notification-sounds/default.mp3')
    if (sound?.file) {
      return sound.file;
    }
    return `${SOUNDS_PATH}/default.mp3`;
  }, []);

  /**
   * Play a notification sound
   */
  const playSound = useCallback(
    async (soundId?: string): Promise<boolean> => {
      // Check if sound should play
      if (!enabled || isMuted) {
        logger.debug('NotificationSound', 'Playback disabled or muted');
        return false;
      }

      if (isInQuietHours()) {
        logger.debug('NotificationSound', 'In quiet hours, skipping playback');
        return false;
      }

      const audio = initAudio();
      if (!audio) return false;

      const soundToPlay = soundId || currentSound;
      const soundUrl = getSoundUrl(soundToPlay);

      try {
        // Stop any currently playing sound
        if (isPlaying) {
          audio.pause();
          audio.currentTime = 0;
        }

        audio.src = soundUrl;
        audio.volume = volume;

        setIsPlaying(true);

        await audio.play();

        // Reset playing state when done
        audio.onended = () => {
          setIsPlaying(false);
        };

        return true;
      } catch (err) {
        // Fallback to Web Audio API beep when sound file is missing
        logger.debug('NotificationSound', 'File not found, using fallback beep');
        try {
          const frequency = soundToPlay === 'alert' ? 880 : soundToPlay === 'subtle' ? 330 : 440;
          await playFallbackBeep(volume, frequency, 0.15);
          setIsPlaying(false);
          return true;
        } catch {
          logger.warn('NotificationSound', 'Fallback beep also failed');
          setIsPlaying(false);
          return false;
        }
      }
    },
    [enabled, isMuted, isInQuietHours, initAudio, currentSound, getSoundUrl, volume, isPlaying]
  );

  /**
   * Preview a sound (for settings)
   */
  const previewSound = useCallback(
    async (soundId: string): Promise<void> => {
      const audio = initAudio();
      if (!audio) return;

      const soundUrl = getSoundUrl(soundId);

      try {
        // Stop any currently playing sound
        audio.pause();
        audio.currentTime = 0;

        audio.src = soundUrl;
        audio.volume = volume;

        setIsPlaying(true);
        await audio.play();

        audio.onended = () => {
          setIsPlaying(false);
        };
      } catch (err) {
        // Fallback to Web Audio API beep when sound file is missing
        logger.debug('NotificationSound', 'Preview file not found, using fallback beep');
        try {
          const frequency = soundId === 'alert' ? 880 : soundId === 'subtle' ? 330 : 440;
          await playFallbackBeep(volume, frequency, 0.15);
        } catch {
          logger.warn('NotificationSound', 'Fallback preview also failed');
        }
        setIsPlaying(false);
      }
    },
    [initAudio, getSoundUrl, volume]
  );

  /**
   * Stop currently playing sound
   */
  const stopSound = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  /**
   * Set volume (0-1)
   */
  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));

    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }

    // Persist via useUserPreferencesObject (syncs to Supabase)
    setSettings({ volume: clampedVolume });
  }, [setSettings]);

  /**
   * Toggle mute
   */
  const toggleMute = useCallback(() => {
    // Persist via useUserPreferencesObject (syncs to Supabase)
    setSettings({ muted: !isMuted });
  }, [setSettings, isMuted]);

  /**
   * Set quiet hours configuration
   */
  const setQuietHoursConfig = useCallback((config: Partial<QuietHours>) => {
    const newConfig = { ...quietHours, ...config };
    // Persist via useUserPreferencesObject (syncs to Supabase)
    setSettings({ quietHours: newConfig });
  }, [setSettings, quietHours]);

  /**
   * Change the default notification sound
   */
  const changeSound = useCallback((soundId: string) => {
    // Persist via useUserPreferencesObject (syncs to Supabase)
    setSettings({ sound: soundId });
  }, [setSettings]);

  // Settings are now loaded via useUserPreferencesObject (from localStorage cache + Supabase sync)

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return {
    // State
    volume,
    currentSound,
    isPlaying,
    isMuted,
    quietHours,
    isInQuietHours: isInQuietHours(),

    // Available sounds
    availableSounds: NOTIFICATION_SOUNDS,

    // Actions
    playSound,
    previewSound,
    stopSound,
    setVolume,
    toggleMute,
    changeSound,
    setQuietHoursConfig,
  };
}

export default useNotificationSound;
